import { useCallback, useContext, useMemo, useState } from 'react';
import {
  FieldErrors,
  FormProvider,
  useForm,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import { ModalBody, ModalFooter } from 'reactstrap';
import { OkButton } from 'src/components/Buttons/OkButton';
import { BoostSolidIconDisplay } from 'src/components/SponsoredListings';
import { TargetRoasInput } from 'src/components/SponsoredListings/TargetRoasInput';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import {
  Content,
  FormatContent,
  getContent,
  useContent,
  useContentContext,
  useFormattedContent,
} from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { getTextFieldState, PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Radio, RadioGroup, Stack, TooltipPopover } from 'src/core/ui';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { CancellableFormFooter } from 'src/modals/common';
import { CancellableFormHeader } from 'src/modals/common/CancellableFormHeader';
import { modalDetails } from 'src/modals/common/Modals.css';
import * as styles from 'src/modals/SponsoredListings/CampaignModal/CampaignModal.css';
import { CampaignModalFooter } from 'src/modals/SponsoredListings/CampaignModal/CampaignModalFooter';
import {
  DEFAULT_BASE_BID,
  DEFAULT_TARGET_ROAS,
  MINIMUM_BASE_BID,
} from 'src/modals/SponsoredListings/sponsoredListings.util';
import { IconsFill, InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FLIGHT_DURATION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  BiddingMode,
  FlightDuration,
  Permission,
  SpendStrategy,
  UiCurrency,
} from 'src/WebApiController';

import * as boostActionsStyles from './BulkBoostEventsDialog.css';
import { EventAccordionSection } from './EventAccordionSection';
import { BulkBoostFormValues } from './useBulkBoostActions';

export type BulkBoostModalProps = {
  onExecute: (formValues: BulkBoostFormValues) => Promise<void>;
  unboostedCount: number;
  pausedCount: number;
  activeCount: number;
  ineligibleCount: number;
  unboostedEvents: Array<{ virtId: string; viagId: number }>;
  pausedEvents: Array<{ virtId: string; viagId: number; campaignId: number }>;
  activeEvents: Array<{ virtId: string; viagId: number; campaignId: number }>;
  ineligibleEvents: string[];
  // Set when this step was reached from a multi-step flow, so exiting
  // confirms even before the local form has been touched
  requireCancelConfirmation?: boolean;
};

type BulkBoostFormState = {
  flightDuration: FlightDuration;
  recurringFlightBudget: number | null;
  biddingMode: BiddingMode;
  baseBid: number | null;
  spendStrategy: SpendStrategy;
  targetRoas: number | null;
};

export const BulkBoostModal = ({
  onExecute,
  unboostedCount,
  pausedCount,
  activeCount,
  ineligibleCount,
  unboostedEvents,
  pausedEvents,
  activeEvents,
  ineligibleEvents,
  requireCancelConfirmation,
}: BulkBoostModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { closeModal } = useContext(ModalContext);
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );
  const hasAdPlatformCreatePermission =
    useUserHasAnyOfPermissions(Permission.AdPlatform_CampaignsCreate) &&
    hasAdPlatformViewPermission;

  const uiCurrency = useMemo(
    () => getUiCurrency(loginContext?.user?.activeAccount?.currencyCode),
    [getUiCurrency, loginContext]
  );

  const methods = useForm<BulkBoostFormState>({
    defaultValues: {
      flightDuration: FlightDuration.Daily,
      recurringFlightBudget: 100,
      biddingMode: BiddingMode.Automatic,
      baseBid: null,
      spendStrategy: SpendStrategy.SteadyHarvest,
      targetRoas: null,
    },
  });

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    formState: { errors, isDirty },
  } = methods;

  const flightDuration = watch('flightDuration');
  const biddingMode = watch('biddingMode');
  const baseBid = watch('baseBid');
  const recurringFlightBudget = watch('recurringFlightBudget');
  const spendStrategy = watch('spendStrategy');
  const targetRoas = watch('targetRoas');

  const requiredText = useContent(ContentId.Required);
  const baseBidExceedsBudgetText = useContent(
    ContentId.BaseBidCannotExceedBudget
  );
  const budgetMustBeGreaterText = useContent(
    ContentId.BudgetMustBeGreaterThanBaseBidError
  );

  const onSubmit = useCallback(
    async (formValues: BulkBoostFormState) => {
      // Validate budget
      if (
        !formValues.recurringFlightBudget ||
        formValues.recurringFlightBudget <= 0
      ) {
        setError('recurringFlightBudget', {
          type: 'required',
          message: requiredText,
        });
        return;
      }

      setIsLoading(true);
      try {
        await onExecute({
          flightDuration: formValues.flightDuration,
          recurringFlightBudget: formValues.recurringFlightBudget,
          biddingMode: formValues.biddingMode,
          baseBid: formValues.baseBid ?? undefined,
          spendStrategy: formValues.spendStrategy,
          targetRoas: formValues.targetRoas ?? undefined,
        });
        closeModal(true);
      } finally {
        setIsLoading(false);
      }
    },
    [
      setError,
      requiredText,
      baseBidExceedsBudgetText,
      budgetMustBeGreaterText,
      onExecute,
      closeModal,
    ]
  );

  const isDisabled = unboostedCount + pausedCount === 0;

  if (!hasAdPlatformCreatePermission) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <CancellableFormHeader
        className={styles.modalHeader}
        showDialogOnCancel={requireCancelConfirmation || isDirty}
        disabled={isLoading}
      />

      <ModalBody>
        <div
          className={`${modalDetails} ${boostActionsStyles.bulkBoostDialogContainer}`}
        >
          <BulkBoostModalHeader
            unboostedCount={unboostedCount}
            pausedCount={pausedCount}
            activeCount={activeCount}
            ineligibleCount={ineligibleCount}
            unboostedEvents={unboostedEvents}
            pausedEvents={pausedEvents}
            activeEvents={activeEvents}
            ineligibleEvents={ineligibleEvents}
          />
          <BulkBoostModalBody
            disabled={isLoading}
            flightDuration={flightDuration}
            biddingMode={biddingMode}
            baseBid={baseBid}
            recurringFlightBudget={recurringFlightBudget}
            spendStrategy={spendStrategy}
            targetRoas={targetRoas}
            setValue={setValue}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            requiredText={requiredText}
            uiCurrency={uiCurrency}
          />
          <CampaignModalFooter />
        </div>
      </ModalBody>

      <ModalFooter>
        <CancellableFormFooter disabled={isLoading} hideCloseButton>
          <OkButton
            className={`${styles.boostButtonCommon} ${boostActionsStyles.boostButton}`}
            variant="regular"
            shape="rect"
            onClick={handleSubmit(onSubmit)}
            disabled={isDisabled || isLoading}
            textContentId={ContentId.BoostEvents}
          />
        </CancellableFormFooter>
      </ModalFooter>
    </FormProvider>
  );
};

const BulkBoostModalHeader = ({
  unboostedCount,
  pausedCount,
  activeCount,
  ineligibleCount,
  unboostedEvents,
  pausedEvents,
  activeEvents,
  ineligibleEvents,
}: {
  unboostedCount: number;
  pausedCount: number;
  activeCount: number;
  ineligibleCount: number;
  unboostedEvents: Array<{ virtId: string; viagId: number }>;
  pausedEvents: Array<{ virtId: string; viagId: number; campaignId: number }>;
  activeEvents: Array<{ virtId: string; viagId: number; campaignId: number }>;
  ineligibleEvents: string[];
}) => {
  const { data } = useCatalogDataContext();
  const [openSections, setOpenSections] = useState<string[]>([]);

  const toBeBoostedLabel = useFormattedContent(
    FormatContentId.NEventsToBeBoosted,
    [`${unboostedCount}`]
  ) as string;
  const pausedLabel = useFormattedContent(
    FormatContentId.NPausedEventsWillBeRestarted,
    [`${pausedCount}`]
  ) as string;
  const activeLabel = useFormattedContent(
    FormatContentId.NEventsAlreadyActive,
    [`${activeCount}`]
  ) as string;
  const ineligibleLabel = useFormattedContent(
    FormatContentId.NEventsNotEligible,
    [`${ineligibleCount}`]
  ) as string;

  // The header count reflects every event routed into the bulk flow, not
  // just the boostable subset — the accordion sections below break it down
  const totalEventCount =
    unboostedCount + pausedCount + activeCount + ineligibleCount;

  const toggleSection = (section: string) => {
    setOpenSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  return (
    <Stack
      direction="column"
      alignItems="center"
      gap="l"
      width="full"
      className={boostActionsStyles.bulkBoostHeaderContainer}
    >
      <BoostSolidIconDisplay size="56px" />
      <div className={styles.boostHeaderText}>
        <FormatContent
          id={FormatContentId.BoostNEvents}
          params={[`${totalEventCount}`]}
        />
      </div>
      {unboostedCount > 0 && (
        <EventAccordionSection
          label={toBeBoostedLabel}
          events={unboostedEvents.map((e) => e.virtId)}
          isOpen={openSections.includes('unboosted')}
          onToggle={() => toggleSection('unboosted')}
          data={data}
        />
      )}
      {(pausedCount > 0 || activeCount > 0) && (
        <EventAccordionSection
          label={[
            pausedCount > 0 ? pausedLabel : null,
            activeCount > 0 ? activeLabel : null,
          ]
            .filter(Boolean)
            .join(', ')}
          events={[
            ...pausedEvents.map((e) => e.virtId),
            ...activeEvents.map((e) => e.virtId),
          ]}
          isOpen={openSections.includes('existing')}
          onToggle={() => toggleSection('existing')}
          data={data}
        />
      )}
      {ineligibleCount > 0 && (
        <EventAccordionSection
          label={ineligibleLabel}
          events={ineligibleEvents}
          isOpen={openSections.includes('ineligible')}
          onToggle={() => toggleSection('ineligible')}
          data={data}
        />
      )}
    </Stack>
  );
};

const BulkBoostModalBody = ({
  disabled,
  flightDuration,
  biddingMode,
  baseBid,
  recurringFlightBudget,
  spendStrategy,
  targetRoas,
  setValue,
  setError,
  clearErrors,
  errors,
  requiredText,
  uiCurrency,
}: {
  disabled: boolean;
  flightDuration: FlightDuration;
  biddingMode: BiddingMode;
  baseBid: number | null;
  recurringFlightBudget: number | null;
  spendStrategy: SpendStrategy;
  targetRoas: number | null;
  setValue: UseFormSetValue<BulkBoostFormState>;
  setError: UseFormSetError<BulkBoostFormState>;
  clearErrors: UseFormClearErrors<BulkBoostFormState>;
  errors: FieldErrors<BulkBoostFormState>;
  requiredText: string;
  uiCurrency: UiCurrency;
}) => {
  const { isDarkMode } = useSiteTheme();
  const contentContext = useContentContext();
  const autoGeneratedText = useContent(ContentId.AutoGenerated);
  const baseBidExceedsBudgetText = useContent(
    ContentId.BaseBidCannotExceedBudget
  );
  const budgetMustBeGreaterText = useContent(
    ContentId.BudgetMustBeGreaterThanBaseBidError
  );

  return (
    <Stack
      direction="column"
      gap="l"
      width="full"
      className={styles.boostBodyContainer}
    >
      {/* Step 1: Campaign Name (disabled, auto-generated) */}
      <Stack direction="column" gap="m" width="full">
        <Stack gap="l" width="full">
          <Stack
            gap="m"
            alignItems="center"
            width="fit"
            className={styles.itemLabel}
          >
            <span>1.</span>
            <Content id={ContentId.CampaignName} />
          </Stack>
          <Stack width="full">
            <PosTextField
              disabled
              value=""
              placeholder={autoGeneratedText}
              type="text"
              inputMode="text"
            />
          </Stack>
        </Stack>
      </Stack>

      {/* Step 2: Bidding */}
      <Stack direction="column" gap="m" width="full">
        <Stack gap="m" alignItems="center" className={styles.itemLabel}>
          <span>2.</span>
          <Content id={ContentId.Bidding} />
        </Stack>
        <RadioGroup
          value={biddingMode}
          disabled={disabled}
          onValueChange={(v: BiddingMode) => {
            setValue('biddingMode', v);

            if (v === BiddingMode.TargetRoas) {
              if (targetRoas == null) {
                setValue('targetRoas', DEFAULT_TARGET_ROAS);
              }
              setValue('baseBid', null);
            } else if (v === BiddingMode.Manual) {
              if (baseBid == null) {
                setValue('baseBid', DEFAULT_BASE_BID);
              }
              setValue('targetRoas', null);
            } else {
              // Automatic
              setValue('baseBid', null);
              setValue('targetRoas', null);
            }
          }}
        >
          <Radio
            disabled={disabled}
            value={BiddingMode.Automatic}
            variant="outline"
            label={<Content id={ContentId.Automatic} />}
            labelSubtext={
              <Content id={ContentId.Automatic_Bidding_Description} />
            }
          />
          <Radio
            disabled={disabled}
            value={BiddingMode.TargetRoas}
            variant="outline"
            label={<Content id={ContentId.Manual} />}
            labelSubtext={<Content id={ContentId.Manual_Bidding_Description} />}
          />
        </RadioGroup>
      </Stack>

      {/* Subsection: Bid Amount + Duration/Budget */}
      <Stack
        direction="column"
        gap="m"
        width="full"
        className={styles.subsectionContainer}
      >
        {/* CPC input - when Manual (Legacy CPC) mode */}
        {biddingMode === BiddingMode.Manual && (
          <Stack gap="l" width="full">
            <Stack gap="l" width="half">
              <Stack
                gap="m"
                alignItems="center"
                width="full"
                className={styles.itemLabel}
              >
                <span>
                  <Content id={ContentId.SetBidAmount} />
                </span>
                <TooltipPopover
                  contentVariant={isDarkMode ? 'light' : 'dark'}
                  triggerContent={
                    <InfoOutlineIcon
                      withHoverEffect
                      fill={IconsFill.iconPrimary}
                      size={vars.iconSize.s}
                    />
                  }
                >
                  <Content id={ContentId.SetBidAmountDescription} />
                </TooltipPopover>
              </Stack>
            </Stack>
            <Stack width="half">
              <PosFormField errors={errors.baseBid?.message}>
                <PosCurrencyField
                  disabled={disabled}
                  errorState={getTextFieldState(errors.baseBid)}
                  value={baseBid ?? ''}
                  uiCurrency={uiCurrency}
                  onChange={(e) => {
                    let newNumber = e.target.value
                      ? Number(e.target.value)
                      : null;

                    if (typeof newNumber !== 'number' || isNaN(newNumber)) {
                      newNumber = null;
                    }

                    setValue('baseBid', newNumber);
                    clearErrors('baseBid');
                  }}
                  onBlur={() => {
                    if (biddingMode === BiddingMode.Manual) {
                      if (baseBid == null) {
                        setError('baseBid', {
                          type: 'required',
                          message: requiredText,
                        });
                      } else if (baseBid < MINIMUM_BASE_BID) {
                        setValue('baseBid', MINIMUM_BASE_BID);
                      }
                    }

                    if (
                      biddingMode === BiddingMode.Manual &&
                      baseBid &&
                      recurringFlightBudget &&
                      baseBid > recurringFlightBudget
                    ) {
                      setError('baseBid', {
                        type: 'max',
                        message: baseBidExceedsBudgetText,
                      });
                    } else {
                      clearErrors('recurringFlightBudget');
                    }
                  }}
                />
              </PosFormField>
            </Stack>
          </Stack>
        )}

        {/* Target ROAS input - when TargetRoas mode */}
        {biddingMode === BiddingMode.TargetRoas && (
          <Stack direction="column" gap="m" width="full">
            <Stack
              gap="m"
              alignItems="center"
              width="full"
              className={styles.itemLabel}
            >
              <span>Target ROAS</span>
              <TooltipPopover
                contentVariant={isDarkMode ? 'light' : 'dark'}
                triggerContent={
                  <InfoOutlineIcon
                    withHoverEffect
                    fill={IconsFill.iconPrimary}
                    size={vars.iconSize.s}
                  />
                }
              >
                <Content id={ContentId.Manual_TargetRoas_Tooltip} />
              </TooltipPopover>
            </Stack>
            <TargetRoasInput
              value={targetRoas ?? DEFAULT_TARGET_ROAS}
              onChange={(v) => {
                setValue('targetRoas', v);
              }}
              disabled={disabled}
            />
          </Stack>
        )}

        {/* Flight Duration + Budget - side by side */}
        <Stack gap="l" width="full">
          <Stack gap="l" width="half">
            <Stack
              gap="m"
              alignItems="center"
              width="full"
              className={styles.itemLabel}
            >
              <PosEnumSelect
                className={styles.itemLabel}
                fontWeight={400}
                disabled={disabled}
                variant="textPlain"
                value={flightDuration ?? FlightDuration.Daily}
                valueOptionsContent={FLIGHT_DURATION_TO_CID}
                sortMode="none"
                displayText={getContent(
                  FLIGHT_DURATION_TO_CID[
                    flightDuration ?? FlightDuration.Daily
                  ],
                  contentContext
                )}
                onChange={(option: FlightDuration | null) => {
                  if (option) {
                    setValue('flightDuration', option);
                  }
                }}
              />
              <TooltipPopover
                contentVariant={isDarkMode ? 'light' : 'dark'}
                triggerContent={
                  <InfoOutlineIcon
                    withHoverEffect
                    fill={IconsFill.iconPrimary}
                    size={vars.iconSize.s}
                  />
                }
              >
                <Content id={ContentId.FlightBudgetDurationHelpInfo} />
              </TooltipPopover>
            </Stack>
          </Stack>
          <Stack width="half">
            <PosFormField errors={errors.recurringFlightBudget?.message}>
              <PosCurrencyField
                disabled={disabled}
                errorState={getTextFieldState(errors.recurringFlightBudget)}
                value={recurringFlightBudget ?? ''}
                uiCurrency={uiCurrency}
                onChange={(e) => {
                  let newNumber = e.target.value
                    ? Number(e.target.value)
                    : null;

                  if (typeof newNumber !== 'number' || isNaN(newNumber)) {
                    newNumber = null;
                  }

                  setValue('recurringFlightBudget', newNumber);
                  clearErrors('recurringFlightBudget');
                }}
                onBlur={() => {
                  if (!recurringFlightBudget || recurringFlightBudget <= 0) {
                    setError('recurringFlightBudget', {
                      type: 'required',
                      message: requiredText,
                    });
                  }

                  // Validate that the base bid cannot be more than the recurring flight budget
                  if (
                    biddingMode === BiddingMode.Manual &&
                    baseBid &&
                    baseBid > (recurringFlightBudget ?? 0)
                  ) {
                    setError('recurringFlightBudget', {
                      type: 'max',
                      message: budgetMustBeGreaterText,
                    });
                  } else {
                    clearErrors('baseBid');
                  }
                }}
              />
            </PosFormField>
          </Stack>
        </Stack>
      </Stack>

      {/* Step 3: Strategy */}
      {biddingMode === BiddingMode.Automatic && (
        <Stack direction="column" gap="m" width="full">
          <Stack gap="m" alignItems="center" className={styles.itemLabel}>
            <span>3.</span>
            <Content id={ContentId.BoostStrategy} />
            <TooltipPopover
              contentVariant={isDarkMode ? 'light' : 'dark'}
              triggerContent={
                <InfoOutlineIcon
                  withHoverEffect
                  fill={IconsFill.iconPrimary}
                  size={vars.iconSize.s}
                />
              }
            >
              <Content id={ContentId.BoostStrategyDescription} />
            </TooltipPopover>
          </Stack>
          <RadioGroup
            value={spendStrategy}
            disabled={disabled}
            onValueChange={(v: SpendStrategy) => {
              setValue('spendStrategy', v);
            }}
          >
            <Radio
              disabled={disabled}
              value={SpendStrategy.HoldForBetterPrices}
              variant="outline"
              label={
                <Content id={ContentId.BoostStrategyHoldForBetterPrices} />
              }
              labelSubtext={
                <Content
                  id={ContentId.BoostStrategyHoldForBetterPricesDescription}
                />
              }
            />
            <Radio
              disabled={disabled}
              value={SpendStrategy.SteadyHarvest}
              variant="outline"
              label={<Content id={ContentId.BoostStrategySteadyHarvest} />}
              labelSubtext={
                <Content id={ContentId.BoostStrategySteadyHarvestDescription} />
              }
              rightContent={
                <span className={styles.itemMetadata}>
                  <Content id={ContentId.Recommended} />
                </span>
              }
            />
            <Radio
              disabled={disabled}
              value={SpendStrategy.MeasuredPush}
              variant="outline"
              label={<Content id={ContentId.BoostStrategyMeasuredPush} />}
              labelSubtext={
                <Content id={ContentId.BoostStrategyMeasuredPushDescription} />
              }
            />
            <Radio
              disabled={disabled}
              value={SpendStrategy.Accelerate}
              variant="outline"
              label={<Content id={ContentId.BoostStrategyAccelerate} />}
              labelSubtext={
                <Content id={ContentId.BoostStrategyAccelerateDescription} />
              }
            />
          </RadioGroup>
        </Stack>
      )}

      {/* Step 4: Target — bulk boosts always target all zones; the default
          selection renders in the active selected state, not disabled */}
      <Stack direction="column" gap="m" width="full">
        <Stack gap="m" alignItems="center" className={styles.itemLabel}>
          <span>4.</span>
          <Content id={ContentId.Target} />
          <TooltipPopover
            contentVariant={isDarkMode ? 'light' : 'dark'}
            triggerContent={
              <InfoOutlineIcon
                withHoverEffect
                fill={IconsFill.iconPrimary}
                size={vars.iconSize.s}
              />
            }
          >
            <Content id={ContentId.CampaignTargetHelpInfo} />
          </TooltipPopover>
        </Stack>
        <RadioGroup value="default" onValueChange={() => {}}>
          <Radio
            value="default"
            variant="outline"
            label={<Content id={ContentId.AllZones} />}
            rightContent={
              <span className={styles.itemMetadata}>
                <Content id={ContentId.Recommended} />
              </span>
            }
          />
        </RadioGroup>
      </Stack>
    </Stack>
  );
};
